import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/base/Header';
import Footer from '../../../components/base/Footer';

export default function InsectRecognitionPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50/30 via-white to-orange-50/20">
      <Header />
      
      <main className="pt-24 pb-20">
        <article className="max-w-4xl mx-auto px-6">
          {/* ヘッダー */}
          <div className="mb-12">
            <Link 
              to="/essay" 
              className="inline-flex items-center gap-2 text-amber-700 hover:text-amber-800 mb-6 transition-colors cursor-pointer"
            >
              <i className="ri-arrow-left-line"></i>
              <span>エッセイ一覧に戻る</span>
            </Link>
            
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
              植物は昆虫を「識別」している
            </h1>
            
            <div className="flex items-center gap-4 text-sm text-gray-600 mb-8">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center">
                  <i className="ri-leaf-line text-white text-lg"></i>
                </div>
                <span className="font-medium text-gray-900">ももちゃん</span>
              </div>
              <span>•</span>
              <time>2026年1月12日</time>
            </div>

            <p className="text-xl text-gray-700 leading-relaxed italic border-l-4 border-amber-500 pl-6 py-2 bg-amber-50/50">
              同じ傷でも、反応はまったく違う
            </p>
          </div>

          {/* アイキャッチ画像 */}
          <div className="mb-12 rounded-2xl overflow-hidden shadow-xl">
            <img 
              src="https://public.readdy.ai/ai/img_res/fdacc2255e5c9e7b1b9e86c89659ca55.jpg" 
              alt="植物による昆虫の識別"
              className="w-full h-[400px] object-cover object-center"
            />
          </div>

          {/* 本文 */}
          <div className="prose prose-lg max-w-none">
            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                植物は、ただ「傷ついたから」反応しているわけではありません。
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                風で葉が裂けたとき。<br />
                人の手で切られたとき。<br />
                そして、昆虫に食べられたとき。
              </p>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                実は植物は、<br />
                <strong className="text-amber-700">その傷が「何によってつけられたのか」</strong>を、はっきりと見分けています。
              </p>

              <p className="text-gray-700 leading-relaxed">
                これは比喩ではなく、<br />
                植物学の分野で繰り返し実証されてきた事実です。
              </p>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-amber-500 to-orange-600 rounded-full"></span>
              同じ傷でも、反応はまったく違う
            </h2>

            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                一見すると、どちらも「葉が傷ついた」状態です。
              </p>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-6 border-l-4 border-gray-400">
                  <div className="flex items-center gap-3 mb-2">
                    <i className="ri-scissors-line text-2xl text-gray-600"></i>
                    <h3 className="text-lg font-bold text-gray-800">ハサミで切った人工的な傷</h3>
                  </div>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl p-6 border-l-4 border-amber-500">
                  <div className="flex items-center gap-3 mb-2">
                    <i className="ri-bug-line text-2xl text-amber-600"></i>
                    <h3 className="text-lg font-bold text-amber-800">昆虫にかじられた自然な傷</h3>
                  </div>
                </div>
              </div>

              <p className="text-gray-700 leading-relaxed mb-6">
                しかし植物の内部では、<br />
                この2つはまったく別の出来事として処理されます。
              </p>

              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <p className="text-gray-700 leading-relaxed mb-2">
                  <strong>人工的な損傷では、</strong>
                </p>
                <p className="text-gray-600 ml-4">
                  局所的な修復反応が起こる程度。
                </p>
              </div>

              <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-lg p-6 mb-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  <strong>一方で昆虫による食害では、</strong><br />
                  植物は一気に防御モードへ切り替わります。
                </p>
                <ul className="space-y-2 ml-4">
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-amber-600 mt-1">•</span>
                    <span>防御物質の合成</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-amber-600 mt-1">•</span>
                    <span>揮発性化合物（VOCs）の放出</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-amber-600 mt-1">•</span>
                    <span>周囲への危険信号の拡散</span>
                  </li>
                </ul>
              </div>

              <div className="bg-amber-100 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed text-center text-lg">
                  まるで、
                </p>
                <p className="text-xl font-bold text-amber-800 text-center my-4">
                  「これは事故ではない。攻撃だ」
                </p>
                <p className="text-gray-700 leading-relaxed text-center">
                  と判断しているかのようです。
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-amber-500 to-orange-600 rounded-full"></span>
              なぜ植物は区別できるのか？
            </h2>

            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                その理由は、意外にもシンプルです。
              </p>

              <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg p-6 mb-6">
                <p className="text-lg font-bold text-amber-800 flex items-start gap-2">
                  <span className="text-2xl">👉</span>
                  <span>昆虫の唾液中に含まれる特定の分子を検知しているから。</span>
                </p>
              </div>

              <p className="text-gray-700 leading-relaxed mb-6">
                昆虫が葉を噛むとき、<br />
                物理的な損傷だけでなく、<br />
                唾液とともにさまざまな化学物質が植物体内に入り込みます。
              </p>

              <p className="text-gray-700 leading-relaxed mb-6">
                これらは総称して<br />
                <strong className="text-amber-700">エリシター（elicitors）</strong>と呼ばれます。
              </p>

              <div className="bg-amber-50 rounded-lg p-6 mb-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  エリシターは、
                </p>
                <ul className="space-y-2">
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-amber-600 mt-1">•</span>
                    <span>昆虫特有</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-amber-600 mt-1">•</span>
                    <span>種類ごとに異なる</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-amber-600 mt-1">•</span>
                    <span>機械では再現できない</span>
                  </li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  という特徴を持っています。
                </p>
              </div>

              <p className="text-gray-700 leading-relaxed mb-4">
                植物はこの"化学的な指紋"を読み取り、
              </p>

              <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg p-6">
                <ul className="space-y-2">
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-amber-600 mt-1 text-xl">✗</span>
                    <span>単なる物理的損傷ではなく</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-amber-600 mt-1 text-xl">✓</span>
                    <span className="font-bold">生物による攻撃</span>
                  </li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  として認識しているのです。
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-amber-500 to-orange-600 rounded-full"></span>
              植物は「相手」を見て対応を変える
            </h2>

            <div className="bg-white rounded-xl p-8 shadow-sm mb-8">
              <p className="text-gray-700 leading-relaxed mb-6">
                重要なのは、<br />
                植物の反応が一律ではないという点です。
              </p>

              <p className="text-gray-700 leading-relaxed mb-6">
                昆虫の種類によって、
              </p>

              <div className="bg-amber-50 rounded-lg p-6 mb-6">
                <ul className="space-y-2">
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-amber-600 mt-1">•</span>
                    <span>出す防御物質</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-amber-600 mt-1">•</span>
                    <span>放出する匂い</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-amber-600 mt-1">•</span>
                    <span>誘引する天敵</span>
                  </li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  が変わることも分かっています。
                </p>
              </div>

              <p className="text-gray-700 leading-relaxed mb-6">
                つまり植物は、
              </p>

              <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg p-6 mb-6">
                <p className="text-xl font-bold text-amber-800 text-center">
                  「誰が、何をしに来たのか」
                </p>
              </div>

              <p className="text-gray-700 leading-relaxed mb-6">
                を判断し、<br />
                最適な対処法を選んでいるのです。
              </p>

              <div className="bg-amber-50 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed mb-4">
                  これは反射的な反応ではありません。<br />
                  情報を受け取り、解釈し、戦略を変える。
                </p>
                <p className="text-gray-700 leading-relaxed">
                  生物学的には、<br />
                  立派な<strong className="text-amber-700">「認知」</strong>と呼べるプロセスです。
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-amber-500 to-orange-600 rounded-full"></span>
              科学的根拠となる代表研究
            </h2>

            <div className="bg-white rounded-xl p-8 shadow-sm mb-8 border-l-4 border-amber-500">
              <p className="text-gray-700 leading-relaxed mb-6">
                この分野を体系的にまとめたのが、次の論文です。
              </p>

              <div className="flex items-start gap-3 mb-6">
                <i className="ri-flask-line text-amber-600 text-2xl"></i>
                <h3 className="text-xl font-bold text-gray-900">Mithöfer et al., Trends in Plant Science, 2005</h3>
              </div>

              <p className="text-gray-700 leading-relaxed mb-4">
                この研究では、
              </p>

              <div className="bg-amber-50 rounded-lg p-6 mb-6">
                <ul className="space-y-2">
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-amber-600 mt-1">•</span>
                    <span>機械損傷</span>
                  </li>
                  <li className="text-gray-700 flex items-start gap-2">
                    <span className="text-amber-600 mt-1">•</span>
                    <span>昆虫食害</span>
                  </li>
                </ul>
                <p className="text-gray-700 leading-relaxed mt-4">
                  を厳密に比較し、<br />
                  防御関連遺伝子の発現パターンが<br />
                  明確に異なることを示しました。
                </p>
              </div>

              <p className="text-gray-700 leading-relaxed mb-4">
                つまり植物は、
              </p>

              <div className="bg-gradient-to-r from-amber-100 to-orange-100 rounded-lg p-6">
                <p className="text-gray-700 leading-relaxed mb-2">
                  <span className="line-through text-gray-500">「傷がついたかどうか」</span>ではなく
                </p>
                <p className="text-lg font-bold text-amber-800">
                  「何によって傷がついたか」
                </p>
                <p className="text-gray-700 leading-relaxed mt-2">
                  を基準に反応しているのです。
                </p>
              </div>
            </div>

            <h2 className="text-3xl font-bold text-gray-900 mt-12 mb-6 flex items-center gap-3">
              <span className="w-2 h-8 bg-gradient-to-b from-amber-500 to-orange-600 rounded-full"></span>
              沈黙の中で行われる、高度な判断
            </h2>

            <div className="bg-gradient-to-r from-amber-600 to-orange-600 text-white rounded-xl p-8 mb-8">
              <p className="text-lg leading-relaxed mb-6">
                私たちはつい、
              </p>
              <p className="text-xl font-bold mb-6 text-center">
                植物＝受け身
              </p>
              <p className="text-lg leading-relaxed mb-6">
                というイメージを持ちがちです。
              </p>
              <p className="text-lg leading-relaxed mb-4">
                しかし実際には、植物は
              </p>
              <ul className="space-y-2 mb-6">
                <li className="flex items-start gap-2">
                  <span className="mt-1">•</span>
                  <span>周囲の状況を読み</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1">•</span>
                  <span>相手を見極め</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-1">•</span>
                  <span>反応を選択する</span>
                </li>
              </ul>
              <p className="text-lg leading-relaxed">
                極めて高度な判断を、<br />
                沈黙のまま行っています。
              </p>
            </div>

            <div className="bg-white rounded-xl p-8 shadow-sm text-center mb-8">
              <p className="text-gray-700 leading-relaxed mb-4">
                声も、表情も、動きもない。<br />
                それでも植物は、確かに状況を理解している。
              </p>
              <p className="text-lg font-bold text-amber-800">
                そう考えると、<br />
                葉に刻まれた小さな傷が、<br />
                まったく違って見えてきませんか？
              </p>
            </div>
          </div>

          {/* 関連記事 */}
          <div className="mt-16 pt-12 border-t border-gray-200">
            <h3 className="text-2xl font-bold text-gray-900 mb-8">関連記事</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <Link 
                to="/blog/plant-communication" 
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-chat-3-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                      植物は本当に「会話」しているのか？
                    </h4>
                    <p className="text-sm text-gray-600">2026年12月15日</p>
                  </div>
                </div>
              </Link>

              <Link 
                to="/blog/air-communication" 
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-windy-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                      空気中の会話
                    </h4>
                    <p className="text-sm text-gray-600">2026年12月22日</p>
                  </div>
                </div>
              </Link>

              <Link 
                to="/blog/internal-communication" 
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-violet-400 to-purple-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-flashlight-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                      植物体内の会話
                    </h4>
                    <p className="text-sm text-gray-600">2026年1月5日</p>
                  </div>
                </div>
              </Link>

              <Link 
                to="/blog/underground-network" 
                className="group bg-white rounded-xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer"
              >
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0">
                    <i className="ri-git-branch-line text-white text-xl"></i>
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-2 group-hover:text-amber-600 transition-colors">
                      地下の会話：菌根菌ネットワーク
                    </h4>
                    <p className="text-sm text-gray-600">2025年12月29日</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}